import { Injectable } from '@angular/core';
import * as appboy from '@braze/web-sdk';
import { BehaviorSubject, interval, merge, switchMap, tap, withLatestFrom } from 'rxjs';
import { Card, Genders, User } from '@braze/web-sdk';
import { environment } from '../../environments/environment';
import { CustomerAuth } from '../../smoothr-web-app-core/models/CustomerAuth';
import { CookieConsentService } from './cookie.service';
import { filter } from 'rxjs/operators';
import Customer from '../../smoothr-web-app-core/models/Customer';
import { HttpClient } from '@angular/common/http';

export enum BrazeEvents {
	open_category = 'open_category',
	open_article = 'open_article',
	article_selected = 'article_selected',
	open_cart = 'open_cart',
	add_more = 'add_more',
	edit_article = 'edit_article',
	remove_article = 'remove_article',
	increase_article_count = 'increase_article_count',
	decrease_article_count = 'decrease_article_count',
	add_promo = 'add_promo',
	make_payment = 'make_payment',
}

@Injectable({
	providedIn: 'root',
})
export class BrazeService {
	contentCards$ = new BehaviorSubject<Card[]>([]);
	truckUserEvents = false;
	currUser: Customer;

	eventsArray: BehaviorSubject<{ email: string; name: string; time: string }[]> = new BehaviorSubject([]);

	constructor(private http: HttpClient) {
		document.addEventListener('visibilitychange', () => {
			if (document.visibilityState === 'hidden') this.sendBrazeEventsChunk();
		});
	}

	public disableSdk() {
		if (appboy.isInitialized()) {
			appboy.disableSDK();
		}
	}

	public initializeService() {
		appboy.initialize(environment.braze.apiKey, {
			baseUrl: environment.braze.url,
			allowUserSuppliedJavascript: true,
			enableLogging: !environment.production, // optional, enable for debugging,
			doNotLoadFontAwesome: true,
		});
		appboy.automaticallyShowInAppMessages();

		// if you use Content Cards
		appboy.requestContentCardsRefresh();
		appboy.subscribeToContentCardsUpdates(cards => {
			// cards have been updated
			this.contentCards$.next(cards.cards as any[]);
		});
		appboy.subscribeToInAppMessage(inAppMessage => {
			console.log(inAppMessage);
		});

		appboy.subscribeToSdkAuthenticationFailures(error => {
			console.log('SdkAuthenticationFailures', error);
		});

		appboy.openSession();
		this.initEventsBatchSender();
	}

	public isPushPermissionGranted() {
		return appboy.isPushPermissionGranted();
	}

	public checkIsSupported() {
		return appboy.isPushSupported();
	}

	public logCustomEvent(eventName: BrazeEvents, properties: any = {}) {
		if (!CookieConsentService.marketing || !this.truckUserEvents) return;

		if (!this.currUser.isAnonymous) appboy.logCustomEvent(eventName, properties);
		else {
			const val = this.eventsArray.value;
			val.push({ email: this.currUser.email, name: eventName, time: new Date().toISOString() });
			this.eventsArray.next(val);
		}
	}

	initUser(user: CustomerAuth) {
		if (!appboy.isInitialized() && CookieConsentService.marketing) this.initializeService();
		this.truckUserEvents = true;
		appboy.changeUser(user.customer.uid);
		this.currUser = user.customer;
	}

	public initForGuestSubscription(id: string) {
		if (!CookieConsentService.marketing) return;
		if (!appboy.isInitialized()) this.initializeService();
		appboy.changeUser(id);
	}

	requestPushPermission() {
		appboy.subscribeToInAppMessage(inpp => {
			console.log(inpp);
		});

		appboy.requestPushPermission(
			(enpoint, publickey, userAuth) => {
				console.log('Push notification permission:', enpoint, publickey, userAuth);
			},
			denied => {
				console.error('Push notification permission error:', denied);
			}
		);
	}

	onCardClick(card: any): void {
		try {
			//appboy.logCardClick(card);

			appboy.logContentCardImpressions([card]);
		} catch (e) {
			console.log(e);
		}
	}

	initEventsBatchSender() {
		const minuteTrigger$ = interval(30_000);
		const sizeTrigger$ = this.eventsArray.pipe(filter(arr => arr.length >= 70));

		merge(minuteTrigger$, sizeTrigger$)
			.pipe(
				withLatestFrom(this.eventsArray),
				filter(([_, arr]) => arr.length > 0),
				switchMap(([_, arr]) => {
					return this.sendBrazeEventsChunk();
				})
			)
			.subscribe();
	}

	public async sendBrazeEventsChunk() {
		if (!this.eventsArray.value.length) return;

		const res = fetch(environment.baseUrl + '/v1/braze/track/user/events', {
			method: 'POST',
			body: JSON.stringify(this.eventsArray.value),
			headers: { 'Content-Type': 'application/json' },
			keepalive: true,
		});
		this.eventsArray.next([]);
		return res;
	}
}
